import { createAudioPlayer, setAudioModeAsync, type AudioPlayer } from "@-/com.npmjs.expo-audio";
import pMemoize from "@-/com.npmjs.p-memoize";

export type PronunciationAudioSource = number | string;

/** One reading's recording, tagged with its tone-number pinyin so listeners know what is playing. */
export type PronunciationAudioTrack = {
  pinyin: string;
  source: PronunciationAudioSource;
};

const configureAudioMode = pMemoize(() =>
  setAudioModeAsync({
    allowsRecording: false,
    interruptionMode: "doNotMix",
    playsInSilentMode: true,
    shouldRouteThroughEarpiece: false,
  }),
);

/**
 * Every mounted `useAudioPlayer` / `createAudioPlayer` eagerly allocates a native
 * `AudioPlayer` — on Android that is a full `ExoPlayer` (decoder buffers, codec
 * surface, native memory). Pinyin sound buttons render many-per-screen, so
 * mounting one player per button used to leave a dozen idle ExoPlayers alive at
 * once; the allocator eventually failed to `mmap` and Scudo aborted the process
 * ("internal map failure (NO MEMORY)"). Pronunciation audio is inherently
 * one-at-a-time (a new tap should replace whatever was playing), so all pinyin
 * playback shares this single lazily-created player instead.
 */
let sharedPlayer: AudioPlayer | null = null;

/**
 * Bumped on every fresh `play`/`stop` so a previously-queued sequence, or a
 * `didJustFinish` event fired during teardown, can detect that it has been
 * superseded and bail without touching the (possibly reused) player.
 */
let playbackEpoch = 0;

type ActiveSequence = {
  epoch: number;
  index: number;
  isMuted: () => boolean;
  queue: readonly PronunciationAudioTrack[];
};

let activeSequence: ActiveSequence | null = null;

/** The tone-number pinyin whose recording the shared player is sounding, if any. */
let nowPlayingPinyin: string | null = null;
const nowPlayingListeners = new Set<() => void>();

function setNowPlayingPinyin(pinyin: string | null): void {
  if (nowPlayingPinyin === pinyin) {
    return;
  }
  nowPlayingPinyin = pinyin;
  for (const listener of nowPlayingListeners) {
    listener();
  }
}

export function getNowPlayingPronunciation(): string | null {
  return nowPlayingPinyin;
}

export function subscribeNowPlayingPronunciation(listener: () => void): () => void {
  nowPlayingListeners.add(listener);
  return () => {
    nowPlayingListeners.delete(listener);
  };
}

function ensureSharedPlayer(): AudioPlayer {
  if (sharedPlayer !== null) {
    return sharedPlayer;
  }
  const player = createAudioPlayer(null, { keepAudioSessionActive: false });
  player.volume = 1;
  player.muted = false;
  player.addListener("playbackStatusUpdate", (status) => {
    if (!status.didJustFinish) {
      return;
    }
    const sequence = activeSequence;
    const nextTrack =
      sequence === null || sequence.epoch !== playbackEpoch || sequence.isMuted()
        ? undefined
        : sequence.queue[sequence.index + 1];
    if (sequence === null || nextTrack === undefined) {
      activeSequence = null;
      setNowPlayingPinyin(null);
      return;
    }
    sequence.index += 1;
    playTrackNow(nextTrack);
  });
  sharedPlayer = player;
  return player;
}

function playTrackNow(track: PronunciationAudioTrack): void {
  const player = ensureSharedPlayer();
  try {
    player.replace(track.source);
    player.play();
    setNowPlayingPinyin(track.pinyin);
  } catch {
    // The native shared object can be torn down under us (fast-refresh, or a
    // release racing an async play). A superseded call must not throw.
    setNowPlayingPinyin(null);
  }
}

/**
 * Play a single pronunciation track, interrupting whatever was playing. `getIsMuted`
 * is read at call time so a mute toggled after the play started still applies to
 * any queued sequence continuation.
 */
export async function playPronunciationTrack(
  track: PronunciationAudioTrack,
  getIsMuted: () => boolean,
): Promise<void> {
  if (getIsMuted()) {
    return;
  }
  const epoch = ++playbackEpoch;
  activeSequence = null;
  await configureAudioMode();
  if (epoch !== playbackEpoch || getIsMuted()) {
    return;
  }
  playTrackNow(track);
}

/**
 * Play each track in order, advancing when the previous one finishes. A later
 * `play*`/`stop` call (higher epoch) cancels the remainder of the sequence.
 */
export async function playPronunciationSequence(
  tracks: readonly PronunciationAudioTrack[],
  getIsMuted: () => boolean,
): Promise<void> {
  if (getIsMuted()) {
    return;
  }
  const firstTrack = tracks[0];
  if (firstTrack === undefined) {
    activeSequence = null;
    return;
  }
  const epoch = ++playbackEpoch;
  activeSequence = { epoch, index: 0, isMuted: getIsMuted, queue: tracks };
  await configureAudioMode();
  if (epoch !== playbackEpoch || getIsMuted()) {
    return;
  }
  playTrackNow(firstTrack);
}

/**
 * Stop the shared player and cancel any in-flight sequence. Safe to call after
 * the player was released; the native player instance is kept for reuse (a new
 * `play*` will `replace` its source) so we never churn ExoPlayers.
 */
export function stopPronunciation(): void {
  playbackEpoch += 1;
  activeSequence = null;
  setNowPlayingPinyin(null);
  if (sharedPlayer === null) {
    return;
  }
  try {
    sharedPlayer.pause();
  } catch {
    // Already released — nothing to pause.
  }
}
